import React, { JSX } from 'react';
import styled, { css } from 'styled-components';
import { palette } from 'styles/color';

type Align = 'center' | 'left' | 'right' | 'justify' | 'initial';
type Weight = 'normal' | 'bold' | 'lighter' | 'bolder' | number;
type TypographyPropsType = {
  children: React.ReactNode;
  component?: keyof JSX.IntrinsicElements;
  size?: string;
  color?: string;
  weight?: Weight;
  align?: Align;
  height?: string | number;
  truncate?: boolean;
  fullWidth?: boolean;
} & React.HTMLAttributes<HTMLElement>;

type StyledTypographyProps = Omit<TypographyPropsType, 'children' | 'component'>;

/**
 * 텍스트를 표현할 때 사용
 * @param {React.ReactNode} children - 표현할 텍스트
 * @param {string} [component='p'] - HTML 태그
 * @param {string} [size] - 폰트 사이즈
 * @param {string} [color] - 폰트 색상
 * @param {Weight} [weight='normal'] - 폰트 굵기
 * @param {Align} [align='left'] - 텍스트 정렬
 * @param {string | number} [height='normal'] - 줄 높이
 * @param {boolean} [truncate=false] - 텍스트가 넘칠 경우 생략 부호(...), 넓이 지정 필요
 * @param {boolean} [fullWidth=false] - 텍스트가 가득 차게 표현
 * @param {React.HTMLAttributes<HTMLElement>} rest - HTML 태그의 기본 속성
 */
const Typography: React.FC<TypographyPropsType> = ({
  children,
  component = 'p', // or 'span' default
  size = '1rem',
  color = palette.black,
  weight = 'normal',
  align = 'left',
  height = '1.5',
  truncate = false,
  fullWidth = false,
  ...rest
}) => {
  return (
    <StyledTypography
      as={component}
      size={size}
      color={color}
      weight={weight}
      align={align}
      height={height}
      truncate={truncate}
      fullWidth={fullWidth}
      {...rest}>
      {children}
    </StyledTypography>
  );
};

export default Typography;

const StyledTypography = styled.p.withConfig({
  shouldForwardProp: (prop) => prop !== 'fullWidth' && prop !== 'truncate'
})<StyledTypographyProps>`
  margin: 0;
  padding: 0;
  color: ${({ color }) => color};
  font-size: ${({ size }) => size || '1rem'};
  font-weight: ${({ weight }) => weight};
  text-align: ${({ align }) => align};
  line-height: ${({ height }) => (typeof height === 'number' ? `${height}px` : height)};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  white-space: pre-wrap;

  ${({ truncate }) =>
    truncate &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
`;
